// Live developer tuning panel. A schema-driven slider panel (groups of knobs) plus a JSON
// export/import box. This is a pure UI module: it knows nothing about water, terrain, or three.js.
// It renders the knob groups it is handed, syncs each slider with a numeric input, and calls the
// provided callbacks. The caller wires a toggle key and the export/import logic.

export interface Knob {
  label: string;
  help?: string;        // plain-language explanation, shown on hover
  get(): number;        // read the current value
  set(v: number): void; // write a new value
  min: number;
  max: number;
  step: number;
  onChange?: () => void; // called AFTER set() when the value changes (terrain knobs pass a debounced regen)
  noRandom?: boolean;    // if true, the Randomize button leaves this knob alone
  randMin?: number;      // Randomize draws within [randMin, randMax] if set, else the full [min, max]
  randMax?: number;      // (terrain knobs use the band spanned by the liked presets)
}

export interface KnobGroup {
  title: string;        // e.g. "Water", "Terrain", "Grade"
  knobs: Knob[];
}

// A row of pick-one buttons (e.g. the water style picker). The active one is highlighted; clicking
// runs onClick (which may reload the world).
export interface ButtonGroup {
  title: string;
  buttons: { label: string; active: boolean; onClick: () => void }[];
}

export interface AdminOptions {
  exportJSON: () => string;            // full settings JSON string to show in the textarea
  importJSON: (text: string) => void;  // parse + apply a pasted settings JSON (may throw on bad JSON)
  buttonGroups?: ButtonGroup[];        // optional pick-one button rows shown at the top of the panel
  topEl?: HTMLElement;                 // optional custom element shown above the knob groups (e.g. a preview)
  title?: string;                      // panel title (default "OXIDE ADMIN")
}

// One bound row: keeps the range slider and the number input pointing at the same knob. `def` is the
// value captured when the panel was built (the boot default), used by the per-row revert button.
interface Row {
  knob: Knob;
  range: HTMLInputElement;
  num: HTMLInputElement;
  def: number;
}

export class AdminPanel {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private textarea!: HTMLTextAreaElement; // the JSON box, refreshed live as knobs change
  private readonly rows: Row[] = [];
  private readonly buttonEls: { el: HTMLButtonElement; cfg: { active: boolean } }[] = []; // pick-one buttons, restyled on refresh()
  private shown = false;

  constructor(private readonly groups: KnobGroup[], private readonly opts: AdminOptions) {
    this.root = document.createElement('div');
    this.root.style.cssText = css(
      'position:fixed', 'top:12px', 'right:12px', 'z-index:10000', 'display:none',
      'box-sizing:border-box', 'width:340px', 'max-height:92vh', 'overflow-y:auto',
      'padding:10px 12px', 'background:rgba(10,12,16,0.92)', 'border:1px solid rgba(120,140,160,0.25)',
      'border-radius:8px', 'font:12px/1.4 ui-monospace,Menlo,Consolas,monospace', 'color:#cfd8e3',
    );

    this.root.appendChild(this.makeTitleBar());

    if (this.opts.topEl) this.root.appendChild(this.opts.topEl);

    for (const g of this.opts.buttonGroups ?? []) this.root.appendChild(this.makeButtonGroup(g));

    this.body = document.createElement('div');
    this.root.appendChild(this.body);
    for (const group of this.groups) this.body.appendChild(this.makeGroup(group));

    const json = this.makeJsonSection();
    this.status = json.status;
    this.root.appendChild(json.section);

    this.guardEvents();
    document.body.appendChild(this.root);
  }

  toggle(): void {
    if (this.shown) this.hide();
    else this.show();
  }

  show(): void {
    this.shown = true;
    this.root.style.display = 'block';
    this.refresh();
  }

  hide(): void {
    this.shown = false;
    this.root.style.display = 'none';
  }

  get visible(): boolean {
    return this.shown;
  }

  // Re-read every knob and update its slider + number input. Called on open and after a JSON import,
  // since values may have changed underneath the panel.
  refresh(): void {
    for (const row of this.rows) {
      const v = row.knob.get();
      row.range.value = String(v);
      row.num.value = fmt(v, row.knob.step);
    }
    for (const { el, cfg } of this.buttonEls) styleButton(el, cfg.active); // reflect a live pick-one change (e.g. grass style)
    this.syncJson();
  }

  // Mirror the current settings into the JSON box, unless the user is editing it (so we never clobber
  // a paste mid-type). Called after any knob change, on open, and after a reset.
  private syncJson(): void {
    if (this.textarea && document.activeElement !== this.textarea) {
      this.textarea.value = this.opts.exportJSON();
    }
  }

  // Randomize each knob in `rows` within its slider range (snapped to step), then commit. onChange
  // fires per knob, but terrain regen is debounced, so a whole group re-rolls in one rebuild.
  private randomizeRows(rows: Row[]): void {
    for (const row of rows) {
      const k = row.knob;
      if (k.noRandom) continue; // locked values (e.g. tuned water colours) are left as-is
      const lo = k.randMin ?? k.min; // terrain knobs roll within the liked-preset band, not full range
      const hi = k.randMax ?? k.max;
      const steps = Math.max(1, Math.round((hi - lo) / k.step));
      const v = clamp(lo + Math.round(Math.random() * steps) * k.step, lo, hi);
      row.range.value = String(v);
      row.num.value = fmt(v, k.step);
      k.set(v);
      k.onChange?.();
    }
    this.syncJson();
    this.setStatus('randomized', '#9aa7b5');
  }

  // Snap every knob back to its boot default.
  private resetAll(): void {
    for (const row of this.rows) {
      row.range.value = String(row.def);
      row.num.value = fmt(row.def, row.knob.step);
      row.knob.set(row.def);
      row.knob.onChange?.();
    }
    this.syncJson();
    this.setStatus('reset to defaults', '#9aa7b5');
  }

  // --- DOM building ---

  private makeTitleBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.style.cssText = css(
      'display:flex', 'justify-content:space-between', 'align-items:center', 'margin-bottom:8px',
    );
    bar.appendChild(span(this.opts.title ?? 'OXIDE ADMIN', 'font-size:13px;letter-spacing:2px;color:#e6ecf2;'));

    const close = document.createElement('button');
    close.textContent = 'x';
    close.title = 'Close';
    close.style.cssText = css(
      'width:20px', 'height:20px', 'line-height:1', 'padding:0', 'background:transparent',
      'color:#9aa7b5', 'border:1px solid rgba(120,140,160,0.3)', 'border-radius:4px',
      'font:inherit', 'cursor:pointer',
    );
    close.addEventListener('click', () => this.hide());
    bar.appendChild(close);
    return bar;
  }

  // A titled row of pick-one buttons (water style picker). The active one is highlighted.
  private makeButtonGroup(g: ButtonGroup): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = css('margin-bottom:8px', 'border-bottom:1px solid rgba(120,140,160,0.15)', 'padding-bottom:6px');
    section.appendChild(span(g.title, 'display:block;font-weight:bold;color:#cdd6e0;margin-bottom:4px;'));
    const row = document.createElement('div');
    row.style.cssText = css('display:flex', 'flex-wrap:wrap', 'gap:4px');
    for (const b of g.buttons) {
      const btn = document.createElement('button');
      btn.textContent = b.label;
      btn.style.cssText = css('flex:1 0 auto', 'padding:5px 7px', 'border-radius:4px', 'font:inherit', 'cursor:pointer');
      styleButton(btn, b.active);
      btn.addEventListener('click', b.onClick);
      // Hover re-checks b.active live, so a button that becomes active (e.g. the grass-style toggle) stops hovering.
      btn.addEventListener('mouseenter', () => { if (!b.active) btn.style.background = 'rgba(120,140,160,0.15)'; });
      btn.addEventListener('mouseleave', () => { if (!b.active) btn.style.background = 'transparent'; });
      this.buttonEls.push({ el: btn, cfg: b });
      row.appendChild(btn);
    }
    section.appendChild(row);
    return section;
  }

  private makeGroup(group: KnobGroup): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = css('margin-bottom:10px');

    const knobs = document.createElement('div');
    knobs.style.cssText = css('display:flex', 'flex-direction:column', 'gap:5px', 'margin-top:5px');

    // Header: a collapse toggle on the left, a per-group Randomize button on the right.
    const header = document.createElement('div');
    header.style.cssText = css(
      'display:flex', 'align-items:center', 'gap:6px',
      'padding:3px 0', 'border-bottom:1px solid rgba(120,140,160,0.15)',
    );
    const title = span('', css('font-weight:bold', 'color:#cdd6e0', 'cursor:pointer', 'user-select:none', 'flex:1'));
    let open = true;
    const render = () => { title.textContent = `${open ? '-' : '+'} ${group.title}`; };
    render();
    title.addEventListener('click', () => {
      open = !open;
      knobs.style.display = open ? 'flex' : 'none';
      render();
    });
    header.appendChild(title);

    // Build the rows first so we can hand this group's slice to its Randomize button.
    const start = this.rows.length;
    for (const knob of group.knobs) knobs.appendChild(this.makeRow(knob));
    const groupRows = this.rows.slice(start);

    const rand = document.createElement('button');
    rand.textContent = '🎲 Random';
    rand.title = `Randomize every ${group.title} setting`;
    rand.style.cssText = css(
      'padding:2px 6px', 'background:transparent', 'color:#9aa7b5',
      'border:1px solid rgba(120,140,160,0.3)', 'border-radius:3px', 'font:inherit', 'cursor:pointer',
    );
    rand.addEventListener('click', () => this.randomizeRows(groupRows));
    header.appendChild(rand);

    section.appendChild(header);
    section.appendChild(knobs);
    return section;
  }

  private makeRow(knob: Knob): HTMLElement {
    const row = document.createElement('div');
    row.style.cssText = css('display:flex', 'align-items:center', 'gap:6px');
    const tip = knob.help ? `${knob.label}: ${knob.help}` : knob.label;
    row.title = tip; // hover anywhere on the row for the plain-language explanation

    const label = span(knob.label, css(
      'flex:0 0 96px', 'overflow:hidden', 'text-overflow:ellipsis', 'white-space:nowrap',
      'color:#9aa7b5', 'cursor:help',
    ));
    label.title = tip;
    row.appendChild(label);

    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(knob.min);
    range.max = String(knob.max);
    range.step = String(knob.step);
    range.style.cssText = css('flex:1', 'min-width:0', 'cursor:pointer');

    const num = document.createElement('input');
    num.type = 'number';
    num.min = String(knob.min);
    num.max = String(knob.max);
    num.step = String(knob.step);
    num.style.cssText = css(
      'flex:0 0 52px', 'box-sizing:border-box', 'padding:2px 3px', 'background:rgba(0,0,0,0.35)',
      'color:#cfd8e3', 'border:1px solid rgba(120,140,160,0.3)', 'border-radius:3px', 'font:inherit',
    );

    const def = knob.get(); // boot default for this knob, captured before any edits
    const setInputs = (v: number) => { range.value = String(v); num.value = fmt(v, knob.step); };
    setInputs(def);

    const commit = (v: number) => { knob.set(v); knob.onChange?.(); this.syncJson(); };
    range.addEventListener('input', () => {
      const v = clamp(parseFloat(range.value), knob.min, knob.max);
      if (Number.isNaN(v)) return;
      num.value = fmt(v, knob.step);
      commit(v);
    });
    num.addEventListener('input', () => {
      const v = clamp(parseFloat(num.value), knob.min, knob.max);
      if (Number.isNaN(v)) return;
      range.value = String(v);
      commit(v);
    });

    // Per-knob revert: snap back to the boot default.
    const revert = document.createElement('button');
    revert.textContent = '↺';
    revert.title = `Reset to default (${fmt(def, knob.step)})`;
    revert.style.cssText = css(
      'flex:0 0 20px', 'height:20px', 'line-height:1', 'padding:0', 'background:transparent',
      'color:#9aa7b5', 'border:1px solid rgba(120,140,160,0.3)', 'border-radius:3px', 'font:inherit', 'cursor:pointer',
    );
    revert.addEventListener('click', () => { setInputs(def); commit(def); });

    row.appendChild(range);
    row.appendChild(num);
    row.appendChild(revert);
    this.rows.push({ knob, range, num, def });
    return row;
  }

  private makeJsonSection(): { section: HTMLElement; status: HTMLDivElement } {
    const section = document.createElement('div');
    section.style.cssText = css('margin-top:10px', 'border-top:1px solid rgba(120,140,160,0.2)', 'padding-top:8px');

    section.appendChild(span(
      'Settings JSON (updates live; Ctrl+A, Ctrl+C to copy; paste + Apply to load)',
      'display:block;color:#7f8b9a;font-size:11px;margin-bottom:5px;',
    ));

    const textarea = document.createElement('textarea');
    this.textarea = textarea;
    textarea.rows = 10;
    textarea.wrap = 'off';
    textarea.spellcheck = false;
    textarea.style.cssText = css(
      'box-sizing:border-box', 'width:100%', 'resize:vertical', 'overflow:auto',
      'padding:6px', 'background:rgba(0,0,0,0.4)', 'color:#cfd8e3',
      'border:1px solid rgba(120,140,160,0.3)', 'border-radius:4px', 'font:inherit',
    );
    section.appendChild(textarea);

    const btnRow = document.createElement('div');
    btnRow.style.cssText = css('display:flex', 'gap:6px', 'margin-top:6px');
    btnRow.appendChild(this.makeButton('Copy', () => {
      textarea.value = this.opts.exportJSON();
      textarea.focus();
      textarea.select();
    }));
    btnRow.appendChild(this.makeButton('Apply', () => {
      try {
        this.opts.importJSON(textarea.value);
        this.setStatus('applied OK', '#5fbf6f');
        this.refresh();
      } catch (err) {
        this.setStatus(err instanceof Error ? err.message : String(err), '#d9656f');
      }
    }));
    btnRow.appendChild(this.makeButton('Reset all', () => this.resetAll()));
    section.appendChild(btnRow);

    const status = document.createElement('div');
    status.style.cssText = css('margin-top:5px', 'min-height:14px', 'font-size:11px', 'color:#7f8b9a');
    section.appendChild(status);

    return { section, status };
  }

  private makeButton(text: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = text;
    b.style.cssText = css(
      'flex:1', 'padding:6px', 'background:#2f5fa8', 'color:#fff', 'border:none',
      'border-radius:4px', 'font:inherit', 'cursor:pointer',
    );
    b.addEventListener('click', onClick);
    b.addEventListener('mouseenter', () => { b.style.background = '#3a72c8'; });
    b.addEventListener('mouseleave', () => { b.style.background = '#2f5fa8'; });
    return b;
  }

  private setStatus(text: string, color: string): void {
    this.status.textContent = text;
    this.status.style.color = color;
  }

  // Keep the panel's own input from reaching the GAME, without blocking the panel's own controls. The
  // game listens on window / the canvas in the BUBBLE phase to drive movement, building, shooting and
  // camera. So we stopPropagation in the bubble phase too: the event still reaches our buttons,
  // sliders and text inputs first (capture + at-target), then we stop it before it bubbles up to the
  // game. Doing this in the CAPTURE phase (the old way) swallowed the panel's own buttons + scrolling.
  // No preventDefault, so the panel scrolls natively and typing into the inputs works.
  private guardEvents(): void {
    const swallow = (e: Event) => e.stopPropagation();
    for (const type of ['keydown', 'keyup', 'pointerdown', 'mousedown', 'mouseup', 'click', 'contextmenu', 'wheel']) {
      this.root.addEventListener(type, swallow); // bubble phase
    }
    // Escape closes the panel (when focus is inside it).
    this.root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hide();
    });
  }
}

// --- helpers ---

function css(...lines: string[]): string {
  return lines.join(';');
}

// Highlight a pick-one button as active (filled blue) or inactive (outline). Shared by build + refresh.
function styleButton(btn: HTMLButtonElement, active: boolean): void {
  btn.style.background = active ? '#3a72c8' : 'transparent';
  btn.style.color = active ? '#fff' : '#9aa7b5';
  btn.style.border = active ? '1px solid #3a72c8' : '1px solid rgba(120,140,160,0.3)';
}

function span(s: string, extra: string): HTMLElement {
  const el = document.createElement('span');
  el.textContent = s;
  el.style.cssText = extra;
  return el;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Decimals from the step: step 0.01 -> 2 decimals, step 1 -> 0. Keeps the numeric readout tidy.
function fmt(v: number, step: number): string {
  const s = String(step);
  const dot = s.indexOf('.');
  const decimals = dot < 0 ? 0 : s.length - dot - 1;
  return v.toFixed(decimals);
}
